// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { Spinner } from '@loa/ui-components';
import openBankingApi from '../../api/openBankingApi';
import type { AccountStatistics, MonthStatistics } from '../../types/openBanking';
import './BankStatisticsChart.css';

interface BankStatisticsChartProps {
  userId: string;
  accountId: string;
  currencyCode: string;
}

const INCOME_COLOR = 'var(--color-success-text)';
const SPENDING_COLOR = 'var(--color-error-text)';

/**
 * 6-month income vs spending for one account, fed by the existing
 * {@code /statistics} endpoint. Renders a grouped bar chart (income
 * left bar, spending right bar per month) plus two stat tiles for
 * average income / average spending across the window.
 *
 * <p>Recharts is the chart library; theme colours come via CSS
 * variables read by recharts' SVG (works because we set the colour
 * tokens at the {@code :root} level).
 */
export function BankStatisticsChart({ userId, accountId, currencyCode }: BankStatisticsChartProps) {
  const [stats, setStats] = useState<AccountStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    openBankingApi.getStatistics(userId, accountId)
      .then((res) => { if (!cancelled) setStats(res); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e : new Error(String(e))); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, accountId]);

  const chartData = useMemo(() => buildChartData(stats?.monthStatistics ?? []), [stats]);

  if (loading) {
    return (
      <div className="bank-statistics-chart__loading">
        <Spinner size="md" text="Loading statistics..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bank-statistics-chart__error">
        Couldn't load statistics: {error.message}
      </div>
    );
  }

  if (!stats || chartData.length === 0) {
    return <div className="bank-statistics-chart__empty">Not enough transaction history yet.</div>;
  }

  return (
    <div className="bank-statistics-chart">
      <div className="bank-statistics-chart__tiles">
        <Tile
          label="Average monthly income"
          amount={stats.averageIncome}
          currency={currencyCode}
          tone="in"
        />
        <Tile
          label="Average monthly spending"
          amount={stats.averageSpending}
          currency={currencyCode}
          tone="out"
        />
      </div>
      <div className="bank-statistics-chart__chart">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 12, right: 16, left: 0, bottom: 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-grey-05)" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatShort(v, currencyCode)} />
            <Tooltip
              formatter={(value) => formatCurrency(Number(value), currencyCode)}
              labelStyle={{ color: 'var(--color-black)' }}
            />
            <Legend />
            <Bar dataKey="income" name="Income" fill={INCOME_COLOR} radius={[4, 4, 0, 0]} />
            <Bar dataKey="spending" name="Spending" fill={SPENDING_COLOR} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Tile({ label, amount, currency, tone }: {
  label: string;
  amount: number;
  currency: string;
  tone: 'in' | 'out';
}) {
  return (
    <div className={`bank-statistics-chart__tile bank-statistics-chart__tile--${tone}`}>
      <div className="bank-statistics-chart__tile-label">{label}</div>
      <div className="bank-statistics-chart__tile-amount">
        {formatCurrency(amount, currency)}
      </div>
    </div>
  );
}

function buildChartData(months: MonthStatistics[]): Array<{ label: string; income: number; spending: number }> {
  const sorted = [...months].sort(
    (a, b) => new Date(a.startOfMonth).getTime() - new Date(b.startOfMonth).getTime(),
  );
  return sorted.map((m) => ({
    label: monthLabel(m.startOfMonth),
    // Server reports spending as a NEGATIVE number; flip for chart
    // so income and spending bars share the same positive Y axis.
    income: round2(m.income),
    spending: round2(Math.abs(m.spending)),
  }));
}

function monthLabel(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  } catch {
    return iso;
  }
}

function formatShort(amount: number, currency: string): string {
  if (Math.abs(amount) >= 1000) {
    return `${(amount / 1000).toFixed(1)}k`;
  }
  return formatCurrency(amount, currency);
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'EUR',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toFixed(0)} ${currency}`;
  }
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
